import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import wordcloud from "highcharts/modules/wordcloud";
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
import fullscreen from "highcharts/modules/full-screen";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import ChartDatadrawer from "../ChartDatadrawer";
import GridLight from "highcharts/themes/grid-light";
import ChartFallbackImg from "./ChartFallbackImg";

// Initialize Highcharts modules
GridLight(Highcharts);
wordcloud(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
fullscreen(Highcharts);

const WordCloudHash = ({ wordcloudData }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedHash, setSelectedHash] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      Highcharts.charts.forEach((chart) => {
        if (chart) chart.reflow();
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (
    !wordcloudData ||
    typeof wordcloudData !== "object" ||
    Array.isArray(wordcloudData) ||
    Object.keys(wordcloudData).length === 0
  ) {
    return <ChartFallbackImg titleText="Trending Scenarios" />;
  }

  // Convert data to array and sort by weight
  const sortedData = Object.entries(wordcloudData)
    .map(([key, value]) => ({
      name: key,
      weight: value,
    }))
    .sort((a, b) => b.weight - a.weight);

  // Calculate dynamic threshold based on data distribution
  const calculateDynamicThreshold = (data) => {
    if (data.length <= 25) return 1;

    const weights = data.map((item) => item.weight);
    const mean = weights.reduce((sum, val) => sum + val, 0) / weights.length;
    const stdDev = Math.sqrt(
      weights.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        weights.length
    );

    // Calculate quartiles for better threshold adjustment
    const sortedWeights = [...weights].sort((a, b) => a - b);
    const q1 = sortedWeights[Math.floor(sortedWeights.length * 0.25)];
    const q3 = sortedWeights[Math.floor(sortedWeights.length * 0.75)];

    if (data.length > 150) {
      return Math.max(q1, mean - 0.5 * stdDev);
    } else if (data.length > 75) {
      return Math.max(3, mean - stdDev);
    } else {
      return Math.max(2, q1);
    }
  };

  // Calculate optimal number of hashtags to display
  const calculateOptimalCount = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportArea = viewportWidth * viewportHeight;

    // Hashtags often need more space due to # symbol
    const baseCount = Math.floor(viewportArea / 13000);
    return Math.min(Math.max(15, baseCount), 80); // Keep between 15 and 80 hashtags
  };

  const threshold = calculateDynamicThreshold(sortedData);
  const optimalCount = calculateOptimalCount();

  // Extended color scheme for hashtags
  const colorScheme = [
    "#0386e5",
    "#6610f2",
    "#6f42c1",
    "#e83e8c",
    "#dc3545",
    "#fd7e14",
    "#f8c346",
    "#28a745",
    "#20c997",
    "#17a2b8",
    "#6c757d",
    "#343a40",
    "#ff5733",
    "#c70039",
    "#900c3f",
    "#581845",
    "#1f77b4",
    "#ff6347",
    "#adff2f",
    "#ff1493",
    "#00fa9a",
    "#8a2be2",
    "#a52a2a",
    "#f4a300",
    "#008080",
    "#483d8b",
    "#e9967a",
  ];

  // Transform and filter data
  const transformedData = sortedData
    .filter((item) => item.weight >= threshold)
    .slice(0, optimalCount)
    .map((item, index) => {
      const maxWeight = sortedData[0].weight;
      return {
        ...item,
        color: colorScheme[index % colorScheme.length],
        fontSize: Math.max(12, (item.weight / maxWeight) * 48 + 8), // Dynamic font sizing
      };
    });

  const handleWordClick = (event) => {
    const hashtag = event.point.name;
    setSelectedHash(hashtag);
    setIsDrawerOpen(true);
  };

  const options = {
    chart: {
      type: "wordcloud",
      backgroundColor: "#ffffff",
      events: {
        load: function () {
          this.reflow();
        },
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: `Trending Scenarios (${transformedData.length} most active)`,
    },
    tooltip: {
      pointFormat:
        '<span style="color:{point.color}">●</span> <h1>{point.weight}</h1><br/>',
      style: {
        fontSize: "20px",
        fontWeight: "bold",
      },
    },
    plotOptions: {
      series: {
        cursor: "pointer",
        point: {
          events: {
            click: handleWordClick,
          },
        },
      },
      wordcloud: {
        minFontSize: 30,
        maxFontSize: 70,
        rotation: {
          from: -15,
          to: 15,
          orientations: 4,
        },
        spiral: "rectangular", // Better for hashtags
        placementStrategy: "center",
      },
    },
    series: [
      {
        type: "wordcloud",
        data: transformedData,
        rotation: {
          from: 0,
          to: 0,
        },
        colorByPoint: true,
      },
    ],
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            "viewData",
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadPDF",
            "downloadSVG",
          ],
        },
      },
      csv: {
        columnHeaderFormatter: (item, key) => {
          if (!item) {
            return key === "name" ? "Keyword" : "Count";
          }
          return false;
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            title: {
              style: {
                fontSize: "16px",
              },
            },
            series: [
              {
                dataLabels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            ],
          },
        },
      ],
    },
  };

  return (
    <>
      <div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <SwipeableDrawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onOpen={() => setIsDrawerOpen(true)} // 🔧 Add this line
        PaperProps={{
          sx: {
            height: "100%",
            overflow: "hidden",
            borderTopLeftRadius: "12px",
            borderBottomLeftRadius: "12px",
          },
        }}
      >
        <Box
          sx={{
            width: 650,
            padding: 2,
            height: "100%",
            overflow: "hidden",
          }}
        >
          {selectedHash && <ChartDatadrawer hashtag={selectedHash} />}
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default WordCloudHash;
